import moment from 'moment';

import { ModelBase } from '../abstract/model-base.model';
import { AnnouncementCategoryEnum } from '../../enums/announcement-category.enum';
import { AnnouncementDisplayModeEnum } from '../../enums/announcement-display-mode.enum';

/**
 * Annonce publiée par Biifor à destination de tous les utilisateurs.
 *
 * Contrat d'API : `b4cash-srv/documentation/announcements/ANNOUNCEMENTS_API.md`
 */
export class Announcement extends ModelBase {

    static override CLASS_NAME: string = 'Announcement';

    private _category!: string;
    private _content!: string;
    private _displayMode!: string;
    private _effectiveDisplayMode!: string;
    private _expiresAt!: moment.Moment;
    private _highlighted!: boolean;
    private _publishedAt!: moment.Moment;
    private _read!: boolean;
    private _title!: string;

    public get category(): string {
        return this._category;
    }

    public set category(value: any) {
        this._setValue('_category', value, 'string');
    }

    /** Libellé de l'étiquette : un seul mot, en majuscules. */
    public get categoryLabel(): string {
        switch (this.category) {
            case AnnouncementCategoryEnum.PRODUCT_NEWS: return 'NOUVEAUTÉ';
            case AnnouncementCategoryEnum.MAINTENANCE: return 'MAINTENANCE';
            case AnnouncementCategoryEnum.INCIDENT: return 'INCIDENT';
            default: return 'ANNONCE';
        }
    }

    public get categorySeverity(): 'primary' | 'warn' | 'danger' | 'secondary' {
        switch (this.category) {
            case AnnouncementCategoryEnum.PRODUCT_NEWS: return 'primary';
            case AnnouncementCategoryEnum.MAINTENANCE: return 'warn';
            case AnnouncementCategoryEnum.INCIDENT: return 'danger';
            default: return 'secondary';
        }
    }

    /**
     * Classe PrimeFlex de couleur du titre : celui-ci porte la couleur de sa catégorie.
     * Dérivée de `categorySeverity` pour que la correspondance ne vive qu'à un seul
     * endroit — ajouter une catégorie ne demande de toucher qu'à ces deux getters.
     *
     * Les nuances ne sont pas celles de l'étiquette : un titre en gros caractères demande
     * un contraste plus soutenu qu'un texte sur fond coloré.
     */
    public get categoryTextClass(): string {
        switch (this.categorySeverity) {
            case 'primary': return 'text-primary-700';
            case 'warn': return 'text-yellow-700';
            case 'danger': return 'text-red-700';
            default: return 'text-color-secondary';
        }
    }

    public get content(): string {
        return this._content;
    }

    public set content(value: any) {
        this._setValue('_content', value, 'string');
    }

    /** Ce qu'a demandé l'administrateur. Ne pilote que l'exigence d'acquittement. */
    public get displayMode(): string {
        return this._displayMode;
    }

    public set displayMode(value: any) {
        this._setValue('_displayMode', value, 'string');
    }

    /** Ce que le front doit faire, préférence utilisateur déjà appliquée par le serveur. */
    public get effectiveDisplayMode(): string {
        return this._effectiveDisplayMode;
    }

    public set effectiveDisplayMode(value: any) {
        this._setValue('_effectiveDisplayMode', value, 'string');
    }

    /** Fin de la mise en avant. L'annonce reste consultable dans l'historique au-delà. */
    public get expiresAt(): moment.Moment {
        return this._expiresAt;
    }

    public set expiresAt(value: any) {
        this._setValue('_expiresAt', value, 'datetime');
    }

    /** À mettre en avant : visible, non lue, non expirée, publiée après l'inscription. */
    public get highlighted(): boolean {
        return this._highlighted;
    }

    public set highlighted(value: any) {
        this._setValue('_highlighted', value, 'boolean');
    }

    /** Annonce critique, au sens du mode d'affichage demandé par l'administrateur. */
    public get isCritical(): boolean {
        return this.displayMode === AnnouncementDisplayModeEnum.CRITICAL;
    }

    /**
     * L'annonce exige-t-elle un acquittement explicite ?
     *
     * Deux cas, qui croisent volontairement les deux axes du contrat : le **mode**
     * critique voulu par l'administrateur, et la **catégorie** maintenance — une
     * interruption de service planifiée doit être vue, quel que soit son mode. La fenêtre
     * perd alors sa croix, l'échappement et la fermeture au clic extérieur : le bouton est
     * le seul moyen d'en sortir.
     */
    public get requiresAcknowledgement(): boolean {
        return this.isCritical || this.category === AnnouncementCategoryEnum.MAINTENANCE;
    }

    /**
     * L'annonce doit-elle s'ouvrir en popup ?
     *
     * Une annonce lue ne repopupe jamais, quel que soit son mode : la popup est une forme
     * de mise en avant, pas un canal distinct.
     */
    public get needsPopup(): boolean {
        return this.highlighted && this.effectiveDisplayMode === AnnouncementDisplayModeEnum.POPUP;
    }

    public get publishedAt(): moment.Moment {
        return this._publishedAt;
    }

    public set publishedAt(value: any) {
        this._setValue('_publishedAt', value, 'datetime');
    }

    public get read(): boolean {
        return this._read;
    }

    public set read(value: any) {
        this._setValue('_read', value, 'boolean');
    }

    public get title(): string {
        return this._title;
    }

    public set title(value: any) {
        this._setValue('_title', value, 'string');
    }

    public override toString(): string {
        return this.title;
    }
}
